import {
  Button,
  ButtonLink,
  DatePickerFieldController,
  Stack,
  TextInputField,
  TextInputFieldController,
} from '@fukuri-admins/ui/common';
import { PasswordTextFieldController } from '@fukuri-admins/ui/common/Form/PasswordTextField/PasswordTextFieldController';
import { getUsersLink } from '~/utils/getLinks';
import styles from './styles.module.scss';

type Props = {
  /** 保存ボタンの非活性フラグ */
  isButtonDisabled: boolean;
  /** フォーム送信ハンドラ */
  onSubmit: React.ComponentProps<'form'>['onSubmit'];
  /**
   * 店番（未連携の場合は '-'）
   *
   * 読み取り専用。フォーム state・バリデーション・送信ペイロードに含めない。
   * PEN-441 で実データに差し替え予定。
   */
  branchCode: string;
  /**
   * 口座番号（未連携の場合は '-'）
   *
   * 読み取り専用。フォーム state・バリデーション・送信ペイロードに含めない。
   * PEN-441 で実データに差し替え予定。
   */
  accountNumber: string;
  /** 利用開始日フィールドの編集可否フラグ */
  isBillableFromEditable: boolean;
  /**
   * 家族招待ユーザーフラグ
   *
   * 【PEN-992 対応】true の場合、利用開始日フィールドを非活性化し、labelType を undefined（任意）にする。
   */
  isInvitee: boolean;
};

/**
 * 利用者編集フォーム（口座連携版）の Presentation コンポーネント
 *
 * Bank 版は氏名・メールアドレス・外部ユーザID・パスワードが編集可能。
 * 店番・口座番号は読み取り専用（disabled）で表示する。
 * 利用開始日フィールドは常時表示する。
 *
 * 項目順: 氏名 → メールアドレス → 店番 → 口座番号 → 外部ユーザID → 利用開始日 → パスワード → パスワード確認
 *
 * @param isButtonDisabled - 保存ボタンの非活性フラグ
 * @param onSubmit - フォーム送信ハンドラ
 * @param branchCode - 店番（PEN-441 で実データに差し替え予定）
 * @param accountNumber - 口座番号（PEN-441 で実データに差し替え予定）
 * @param isBillableFromEditable - 利用開始日の編集可否フラグ
 * @param isInvitee - 家族招待ユーザーフラグ（【PEN-992 対応】）
 */
export const EditUserFormBankAccountPresentation: React.FC<Props> = ({
  isButtonDisabled,
  onSubmit,
  branchCode,
  accountNumber,
  isBillableFromEditable,
  isInvitee,
}) => (
  <form onSubmit={onSubmit}>
    <Stack direction='column' gap={24} width='466px'>
      <h2 className={styles.formTitle}>利用者の編集</h2>
      <TextInputFieldController
        name='fullName'
        fieldWrapperProps={{
          labelType: 'required',
          labelText: '氏名',
        }}
        textInput={{
          placeholder: '山田 太郎',
        }}
      />
      <TextInputFieldController
        name='email'
        fieldWrapperProps={{
          labelType: 'required',
          labelText: 'メールアドレス',
        }}
        textInput={{
          type: 'email',
          placeholder: 'xxx@xxxx.xx.xx',
        }}
      />
      <TextInputField
        fieldWrapperProps={{
          htmlFor: 'branchCode',
          labelText: '店番',
        }}
        textInput={{ disabled: true, value: branchCode }}
      />
      <TextInputField
        fieldWrapperProps={{
          htmlFor: 'accountNumber',
          labelText: '口座番号',
        }}
        textInput={{ disabled: true, value: accountNumber }}
      />
      <TextInputFieldController
        name='externalUserId'
        fieldWrapperProps={{
          labelType: 'optional',
          labelText: '外部ユーザID',
        }}
        textInput={{
          placeholder: 'xxxx',
        }}
      />
      <DatePickerFieldController
        name='billableFrom'
        fieldWrapperProps={{
          labelText: '利用開始日',
          labelType: isInvitee ? undefined : 'required',
          disabled: isInvitee,
        }}
        datePicker={{ disabled: !isBillableFromEditable || isInvitee }}
      />
      <PasswordTextFieldController
        name='password'
        fieldWrapperProps={{
          labelType: 'optional',
          labelText: 'パスワード',
          beforeMessage: '変更する場合のみ入力してください。',
        }}
      />
      <PasswordTextFieldController
        name='passwordConfirmation'
        fieldWrapperProps={{
          labelType: 'optional',
          labelText: 'パスワード確認',
        }}
      />
      <Stack direction='row' gap={16}>
        <ButtonLink to={getUsersLink()} variant='outline' size='md'>
          キャンセル
        </ButtonLink>
        <Button type='submit' size='md' disabled={isButtonDisabled}>
          保存
        </Button>
      </Stack>
    </Stack>
  </form>
);
